var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import classNames from "classnames";
import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { QuiFieldTextSize } from "../_forms/field-types.js";
import { QuiLineHeightsPx } from "../../tokens/typography.js";
import { QuiFlexH } from "../_primitives/QuiFlex/QuiFlex.js";
import { clamp } from "../../utils/index.js";
const fieldLineHeight = {
    xs: QuiLineHeightsPx[QuiFieldTextSize["xs"]],
    sm: QuiLineHeightsPx[QuiFieldTextSize["sm"]],
    md: QuiLineHeightsPx[QuiFieldTextSize["md"]],
    lg: QuiLineHeightsPx[QuiFieldTextSize["lg"]],
    xl: QuiLineHeightsPx[QuiFieldTextSize["xl"]],
};
// these values come from textarea.scss
const verticalPadding = {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 16,
    xl: 24,
};
const useTextArea = ({ value, size, maxLines, lines }) => {
    const [charCount, setCharCount] = useState((value === null || value === void 0 ? void 0 : value.length) || 0);
    const textareaRef = useRef(null);
    const [paddingSize, lineHeight] = useMemo(() => {
        const padding = verticalPadding[size];
        const lineHeight = fieldLineHeight[size];
        return [padding, lineHeight];
    }, [size]);
    useEffect(() => {
        var _a, _b;
        setCharCount((_b = (_a = textareaRef.current) === null || _a === void 0 ? void 0 : _a.value.length) !== null && _b !== void 0 ? _b : 0);
        if (!textareaRef.current) {
            return;
        }
        if (maxLines !== undefined) {
            // set to 1 line high
            textareaRef.current.style.height = `${lineHeight}px`;
            // then calculate how many lines the text takes up and clamp it between lines and maxLines
            const numLines = clamp(Math.floor(textareaRef.current.scrollHeight / lineHeight), Math.max(lines !== null && lines !== void 0 ? lines : 2, 1), Math.max(maxLines !== null && maxLines !== void 0 ? maxLines : Infinity, 1));
            // then set the height to that many lines with padding
            textareaRef.current.style.height = `${numLines * lineHeight + paddingSize}px`;
        }
        else {
            textareaRef.current.style.height = `${Math.max(lines !== null && lines !== void 0 ? lines : 2, 1) * lineHeight + paddingSize}px`;
        }
    }, [value, lines, maxLines, lineHeight, paddingSize]);
    return { charCount, textareaRef };
};
/** Text area component with char count */
export const QuiTextArea = forwardRef((_a, ref) => {
    var { value, onChange, disabled, maxLength, showCount, className, size = "md", invalid, maxLines, lines } = _a, otherProps = __rest(_a, ["value", "onChange", "disabled", "maxLength", "showCount", "className", "size", "invalid", "maxLines", "lines"]);
    const { charCount, textareaRef } = useTextArea({ maxLines, lines, value, size });
    const showCharacterCount = maxLength !== undefined && !!showCount;
    const limitReached = maxLength && charCount === maxLength;
    const wrapperClassName = classNames(`qui-textarea`, `qui-textarea-${size}`, className, { "qui-invalid": invalid });
    return (React.createElement("div", { className: wrapperClassName },
        React.createElement("textarea", Object.assign({ disabled: disabled, style: { resize: "none" }, maxLength: maxLength, ref: (node) => {
                textareaRef.current = node;
                if (ref) {
                    if (typeof ref === "function") {
                        ref(node);
                    }
                    else {
                        ref.current = node;
                    }
                }
            }, value: value, onChange: (e) => {
                if (onChange) {
                    onChange(e.target.value);
                }
            } }, otherProps)),
        showCharacterCount && (React.createElement(QuiFlexH, { className: `qui-text-xs qui-color-black-600 ${showCount === "right" ? "count--right" : ""}` },
            limitReached && React.createElement("span", null, `Character limit reached: ${maxLength}`),
            !limitReached && React.createElement("span", null, charCount + "/" + (maxLength === null || maxLength === void 0 ? void 0 : maxLength.toString()))))));
});
QuiTextArea.displayName = "QuiTextArea";
